import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Draggable from 'react-draggable';
import {
  GridIcon,
  Text3DIcon,
  MeasureIcon,
  DraggableIcon,
  ClipSectionIcon,
  ResetCameraIcon,
  ExpandIcon,
  OverlayIcon,
  HideUnChangeIcon,
  ExplodeAssemblyIcon,
  ComparisonColorIcon,
} from 'icons';
import { useClientStore } from 'apps/hooks';
import { useViewerWidth } from 'libs/rendering/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { analytics } from 'core/analytics';
import MobileToolBar from 'libs/rendering/gltf/components/toolBar/mobileToolbar';
import ButtonGroup from 'components/ButtonGroup';
import { Tooltip } from 'components';
import RangeSlider from 'libs/rendering/gltf/components/toolBar/rangeSlider';
import {
  FloatingMeasurementWindow,
  FloatingWindow,
} from 'libs/rendering/gltf/components/floatingWindow';
import { SectionBrowser } from 'libs/rendering/gltf/components/sectionBrowser';
import { CADSidebarWidth, FrameHeaderHeight } from 'styles/constants';
import { useCADManager } from 'hooks';
import { renderCustomIcon } from 'utils/jsxUtils';

import {
  MainToolBar,
  ToolBarButtons,
  ToolbarDialogStyled,
  ToolbarDialogOptionStyled,
  IconStyled,
  LabelStyled,
} from './toolBar.styled';

const initialToolbarPosition = {
  x: -80,
  y: -40,
};

export const ToolBar = observer(function _ToolBar(props) {
  const clientStore = useClientStore();

  const [isExpanded, setIsExpanded] = useState(true);

  const {
    viewerControl,
    closeMeasurementFloatWindow,
    itemMeasurements,
    itemId,
  } = useCADManager();
  const [, rightOffset] = useViewerWidth();

  const { viewer, cadFile } = viewerControl || {};
  const toolbarOffset = rightOffset / 2;

  const { settings, engine, sectionManager, selectionManager, module } =
    viewer || {};
  const { assembly } = cadFile || {};

  const currCamera = settings?.CurrentCamera();
  const tabLoadingStatus = currCamera?.GetLoadStatus();
  const selMode = selectionManager?.GetSelectionMode();
  const selModeEnum = module?.SelectionMode;

  const isDiffing = viewer?.isDiffLoading || viewer?.isDiffing;

  const measureCount = itemMeasurements.get(itemId).length;

  function toggleGrid() {
    if (props.mobileMode) viewerControl.SetLightModalVisibility(false);
    settings?.ToggleGridVisibility();
  }

  // Section
  function toggleSection() {
    if (sectionManager) {
      sectionManager.SetActive(!sectionManager.IsActive());
      if (sectionManager.IsActive()) {
        sectionManager.SwitchOnClipSection(viewerControl.defaultClipPlaneType);
        viewerControl.toolBarExpanded = null;
      } else {
        sectionManager.SwitchOffClipSection(1);
      }
    }
  }

  // BOX MEASURE
  function toggleBoxMeasure() {
    if (props.mobileMode) viewerControl.SetLightModalVisibility(false);
    const tool = settings?.GetBoxMeasureTool();
    if (tool) {
      tool.SetVisible(!tool.show);

      if (isDiffing && viewer.diffTool) {
        // diff model has its own box masure tool...
        const diffTool = settings?.GetDiffBoxMeasureTool();
        if (diffTool) {
          diffTool.SetVisible(!diffTool.show);
        }
      }
    }
    viewerControl.ForceRender();
  }

  // EXPLODE
  function toggleExplodeMenu() {
    if (props.mobileMode) viewerControl.SetLightModalVisibility(false);
    viewerControl.ToggleExplodeMode();
    analytics.sendEvent('viewer-explode-toggle');
  }

  function updateOnChangeRangeSlider(event) {
    const magnitude = parseFloat(event.target.value) / 50.0;
    settings?.SetExplodeQ(magnitude);
  }

  // Diffing
  function toggleDiffMode() {
    if (isDiffing && viewer.diffTool) {
      viewer.diffTool.ToggleDiffMode();
      // selection logic changes, so clean it up...
      viewer.selectionManager.ClearSelection();
    }
  }

  // diff colors
  function toggleDiffColors() {
    if (isDiffing && viewer.diffTool && viewer.diffTool.hasResult) {
      viewer.diffTool?.ToggleUseVaoBitmask();
    }
  }

  function toggleShowDiffUnchanged() {
    if (isDiffing && viewer.diffTool && viewer.diffTool.isAssemblyDiff) {
      viewer.diffTool.ToggleSkipUnchanged();
    }
  }

  const isActiveGrid = settings?.IsGridVisible();
  const isActiveMeasure = settings?.GetBoxMeasureTool()?.show;
  const isDisabledMeasure =
    sectionManager.IsRunning() || engine?.ExplodeRuning();

  const isActiveExplodeAssembly = engine?.ExplodeRuning();
  const isActiveClipSection = sectionManager.IsActive();

  const isSkipUnchanged = viewer.diffTool?.skipUnchanged;
  const isActiveColorSwitch = viewer.diffTool?.useVaoBitmask;
  const navCubeVisible = viewerControl?.viewerVisible;

  function configList() {
    return [
      {
        icon: 'reset_camera',
        renderIcon: isHover =>
          renderCustomIcon(ResetCameraIcon, {
            isHover,
          }),
        viewBox: '0 0 40 40',
        tooltip: 'Reset camera',
        onClick: () => {
          if (viewer) {
            if (props.mobileMode) viewerControl.SetLightModalVisibility(false);
            settings.Reset();
            assembly.RestoreVisibility();
            viewerControl.ForceRender();
          }
        },
        isVisible: navCubeVisible,
        isHideOnMobile: true,
      },
      {
        icon: 'grid',
        viewBox: '0 0 40 40',
        renderIcon: isHover =>
          renderCustomIcon(GridIcon, {
            isHover,
            isActive: isActiveGrid,
          }),
        tooltip: 'Grid',
        onClick: () => toggleGrid(),
        isActive: isActiveGrid,
        isVisible: currCamera?.Is3D() && !currCamera.IsPlaneXY(),
        endGroup: true,
        mobileCustomScale: 1.2,
      },
      {
        icon: 'boxMeasure',
        renderIcon: isHover =>
          renderCustomIcon(MeasureIcon, {
            isHover,
            isActive: isActiveMeasure,
            isDisabled: isDisabledMeasure,
          }),
        viewBox: '0 0 40 40',
        tooltip: 'Box measurements',
        onClick: () => toggleBoxMeasure(),
        isActive: isActiveMeasure,
        isVisible: currCamera?.Is3D() && !currCamera.IsPlaneXY(),
        endGroup: true,
        mobileCustomScale: 1.2,
        isDisabled: isDisabledMeasure,
      },
      {
        icon: 'explode_layers',
        renderIcon: isHover =>
          renderCustomIcon(ExplodeAssemblyIcon, {
            isHover,
            isActive: isActiveExplodeAssembly,
          }),
        viewBox: '0 0 40 40',
        tooltip: 'Explode Assembly',
        onClick: () => toggleExplodeMenu(),
        isActive: isActiveExplodeAssembly,
        isVisible:
          !isDiffing &&
          currCamera?.Is3D() &&
          !currCamera.IsPlaneXY() &&
          !currCamera?.IsInstance(), // only if assembly
        mobileCustomScale: 1.6,
      },
      {
        icon: 'clip_section',
        renderIcon: isHover =>
          renderCustomIcon(ClipSectionIcon, {
            isHover,
            isActive: isActiveClipSection,
          }),
        viewBox: '0 0 24 24',
        tooltip: 'Section View',
        onClick: () => toggleSection(),
        isActive: isActiveClipSection,
        isVisible:
          !isDiffing &&
          currCamera?.Is3D() &&
          !currCamera.IsPlaneXY() &&
          currCamera?.IsInstance(),
        mobileCustomScale: 2,
      },
      // Diffing
      {
        icon: viewer.diffTool?.isSuperposed ? 'superposed' : 'side_by_side',
        renderIcon: isHover =>
          !viewer.diffTool?.isSuperposed
            ? renderCustomIcon(OverlayIcon, {
                isHover,
                isActive: viewer.diffTool?.isSuperposed,
              })
            : null,
        viewBox: '0 0 40 40',
        tooltip: !viewer.diffTool?.isSuperposed ? 'Overlay' : 'Two sided',
        onClick: () => toggleDiffMode(),
        isActive: viewer.diffTool?.isSuperposed,
        isVisible: isDiffing && !viewer.isDiffLoading,
        mobileCustomScale: 2,
      },
      {
        icon: 'color_switch',
        renderIcon: isHover =>
          renderCustomIcon(ComparisonColorIcon, {
            isHover,
            isActive: isActiveColorSwitch,
          }),
        viewBox: '0 0 40 40',
        tooltip: 'CAD Comparison Colors',
        onClick: () => toggleDiffColors(),
        isActive: isActiveColorSwitch,
        isVisible: isDiffing && viewer?.diffTool?.hasResult,
        mobileCustomScale: 2,
      },
      {
        icon: isSkipUnchanged ? 'diff_showUnchanged' : 'diff_hideUnchanged',
        renderIcon: isHover =>
          !isSkipUnchanged
            ? renderCustomIcon(HideUnChangeIcon, {
                isHover,
                isActive: isSkipUnchanged,
              })
            : null,
        viewBox: '0 0 40 40',
        tooltip: !isSkipUnchanged ? 'Hide unchanged' : 'Show unchanged',
        onClick: () => toggleShowDiffUnchanged(),
        isActive: isSkipUnchanged,
        isVisible: isDiffing && viewer.diffTool.isAssemblyDiff,
        mobileCustomScale: 2,
      },

      {
        icon: 'text_3d',
        renderIcon: isHover =>
          renderCustomIcon(Text3DIcon, {
            isHover,
            isActive: settings.textOnScreen,
          }),
        viewBox: '-1 -1 42 42',
        tooltip: '3D Text facing screen',
        onClick: () => {
          if (settings) {
            settings.textOnScreen = !settings?.textOnScreen;
          }
        },
        isActive: settings.textOnScreen,
        isVisible: measureCount > 0,
        isHideOnMobile: true,
      },
    ].filter(e => !!e);
  }

  const [currentPosition, setPosition] = useState(initialToolbarPosition);

  const collapse = () => {
    setPosition({
      x: clientStore.windowWidth / 2 - FrameHeaderHeight - rightOffset,
      y: -40,
    });
  };

  const expand = () => {
    setPosition({
      ...initialToolbarPosition,
      x: initialToolbarPosition.x - toolbarOffset,
    });
  };
  // update diff colors once diff ready
  useEffect(() => {
    if (viewer?.diffTool?.hasResult) {
      if (!viewer.diffTool.useVaoBitmask) {
        // enable diff colors
        setTimeout(() => {
          viewer.diffTool?.ToggleUseVaoBitmask();
        }, 500);
      }
    }
  }, [viewer?.diffTool?.hasResult]);

  useEffect(() => {
    if (isExpanded) {
      expand();
    } else {
      collapse();
    }
  }, [rightOffset]);

  if (props.mobileMode) {
    return <MobileToolBar configList={configList()} />;
  }

  // render
  if (!currCamera || tabLoadingStatus !== 0) return null;
  const canvasEl = document.getElementById(viewerControl.canvasId);
  const iconWrapperStyle = {
    width: 40,
    height: 40,
  };

  const shouldShowMeasurementWindow =
    selMode === selModeEnum.SelectionModeMeasurements;

  const updatePosition = (_, data) => {
    setPosition({
      x: currentPosition.x + data.deltaX,
      y: currentPosition.y + data.deltaY,
    });
  };

  return (
    <>
      <Draggable
        position={currentPosition}
        handle='#dragTrigger'
        onDrag={updatePosition}
        bounds={`#${viewerControl.canvasId}`}
      >
        <MainToolBar
          data-testid='CADPage-toolsBar'
          style={{
            left: `calc(50% - ${CADSidebarWidth}px)`,
          }}
        >
          {isExpanded ? (
            <ToolBarButtons>
              <ButtonGroup buttonGroups={[configList()]} />
            </ToolBarButtons>
          ) : (
            <div
              onClick={() => {
                setIsExpanded(true);
                expand();
              }}
              data-for='expandTooltip'
              data-tip='Expand'
              className='flex-center cursor-pointer'
              style={iconWrapperStyle}
            >
              <Tooltip delayShow={500} place='top' id='expandTooltip' />
              <ExpandIcon />
            </div>
          )}

          {isExpanded && (
            <>
              <div
                onClick={() => {
                  setIsExpanded(false);

                  collapse();
                }}
                data-for='collapseTooltip'
                data-tip='Collapse'
                className='flex-center cursor-pointer border-l border-wfGrey350'
                style={{ ...iconWrapperStyle, width: 20 }}
              >
                <Tooltip delayShow={500} place='bottom' id='collapseTooltip' />
                <ExpandIcon className='rotate-180' />
              </div>
              <DraggableIcon id='dragTrigger' className=' hover:cursor-move' />
            </>
          )}
        </MainToolBar>
      </Draggable>

      {!currCamera?.IsInstance() && engine?.ExplodeRuning() && (
        <RangeSlider
          canvasWidth={canvasEl?.clientWidth}
          updateOnChange={updateOnChangeRangeSlider}
          toggleExplodeMenu={toggleExplodeMenu}
          value={viewer?.engine?.GetExplodeQ()}
        />
      )}

      {sectionManager.IsRunning() ? (
        <FloatingWindow
          title='Section View'
          inner={
            <div className=' px-4 pt-2 mb-4'>
              <SectionBrowser />
            </div>
          }
          onClose={toggleSection}
        />
      ) : (
        shouldShowMeasurementWindow && (
          <div>
            <FloatingMeasurementWindow
              title='New measurement'
              onClose={closeMeasurementFloatWindow}
            />
          </div>
        )
      )}
    </>
  );
});

ToolBar.propTypes = {
  mobileMode: PropTypes.bool,
};

// Pin Dialog
export const PinDialog = observer(() => {
  const viewerControl = useViewerControl();

  const camera = viewerControl.viewer.settings.CurrentCamera();
  if (!camera) {
    return null;
  }

  const pmi = viewerControl.cadFile?.pmi;
  const hasSeparation = camera.IsInstance()
    ? camera?.GetInstance()?.GetBody()?.HasSeparation()
    : pmi?.MeshSeparationCount() > 0;

  const restrictedCamera = camera.Is2D() || camera.IsPlaneXY();
  const showAnnotatePart =
    !restrictedCamera &&
    ((camera.Is3D() && !camera.IsInstance()) ||
      (camera.Is3D() && camera.IsInstance() && !hasSeparation));
  const showAnnotateFeature = restrictedCamera || hasSeparation;

  const selMode = viewerControl.viewer?.selectionManager.GetSelectionMode();
  const selModeEnum = viewerControl.viewer?.module.SelectionMode;

  function startPinAddition(isFeature) {
    const selectionEnum = viewerControl.viewer.module.SelectionMode;
    // init pin selection...
    if (isFeature) {
      // geometric feature (face, edge) selection mode
      if (selMode === selModeEnum.SelectionModeLink) {
        // deactivate
        viewerControl.SyncSelectionMode();
      } else {
        // activate
        viewerControl.viewer.selectionManager.SetSelectionMode(
          selectionEnum.SelectionModeLink,
        );
      }
      // instance (part) selection mode
    } else if (selMode === selModeEnum.SelectionModeInstanceLink) {
      // deactivate
      viewerControl.SyncSelectionMode();
    } else {
      // activate
      viewerControl.viewer.selectionManager.SetSelectionMode(
        selectionEnum.SelectionModeInstanceLink,
      );
    }

    // force all pins visible
    viewerControl.viewer.notePad.visible = true;
  }

  return (
    <ToolbarDialogStyled>
      {showAnnotatePart && (
        <ToolbarDialogOptionStyled
          active={selMode === selModeEnum.SelectionModeInstanceLink}
          onClick={() => {
            startPinAddition(false);
            analytics.sendEvent('viewer-annotate-part');
          }}
        >
          <IconStyled name='annotation_part' viewBox='0 0 40 40' />
          <LabelStyled>Annotate part</LabelStyled>
        </ToolbarDialogOptionStyled>
      )}

      {showAnnotateFeature && (
        <ToolbarDialogOptionStyled
          active={selMode === selModeEnum.SelectionModeLink}
          onClick={() => {
            startPinAddition(true);
            analytics.sendEvent('viewer-annotate-feature');
          }}
        >
          <IconStyled name='annotation_feature' viewBox='0 0 40 40' />
          <LabelStyled>Annotate feature</LabelStyled>
        </ToolbarDialogOptionStyled>
      )}
    </ToolbarDialogStyled>
  );
});
